import { oc } from "@orpc/contract";
import { z } from "zod";
import { InboundEmailServiceSchema } from "@erp/shared-types";

const UUIDSchema = z.uuid();
const InboundEmailSortColumnSchema = z.enum([
  "createdAt",
  "updatedAt",
  "receivedAt",
  "from",
  "to",
  "subject",
  "receivedByMailbox",
  "messageId",
]);

export const ListInboundEmailClassificationSchema = z.object({
  id: UUIDSchema,
  classificationResult: z.string(),
  confidenceScore: z.number(),
  reasoning: z.string().nullable(),
  createdAt: z.coerce.date(),
});

export const InboundEmailListItemSchema = z.object({
  id: UUIDSchema,
  messageId: z.string(),
  from: z.string(),
  to: z.string(),
  cc: z.string().nullable().optional(),
  subject: z.string().nullable().optional(),
  textContent: z.string().nullable().optional(),
  htmlContent: z.string().nullable().optional(),
  spamScore: z.number().nullable().optional(),
  spamReport: z.string().nullable().optional(),
  senderIp: z.string().nullable().optional(),
  spf: z.string().nullable().optional(),
  dkim: z.string().nullable().optional(),
  attachmentCount: z.number().nullable().optional(),
  service: InboundEmailServiceSchema,
  receivedByMailbox: z.string().nullable().optional(),
  receivedAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const InboundEmailListPageInfoSchema = z.object({
  currentPage: z.int().positive(),
  totalPages: z.int().nonnegative(),
  totalCount: z.int().nonnegative(),
  hasNextPage: z.boolean(),
  hasPreviousPage: z.boolean(),
});

/**
 * Shortest body search term the backend will run.
 *
 * The trgm GIN index on `text_content` needs three characters to produce a
 * trigram; below that Postgres falls back to a sequential scan that detoasts
 * every email body. Lives in the contract so the UI can gate on the same
 * number the handler enforces, instead of the two drifting apart.
 */
export const BODY_MIN_SEARCH_LENGTH = 3;

export const ListInboundEmailsInputSchema = z.object({
  page: z.int().min(1).optional(),
  pageSize: z.int().min(1).max(100).optional(),
  search: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  cc: z.string().optional(),
  subject: z.string().optional(),
  /**
   * Substring match against the email body (`text_content`).
   *
   * Include-only — a leading `-` is literal text, not an exclusion — and terms
   * shorter than 3 characters are ignored. Both rules exist because the trgm
   * index that makes this affordable cannot serve `NOT ILIKE` or sub-trigram
   * terms. See `buildBodyFilter`.
   */
  body: z.string().optional(),
  upn: z.string().optional(),
  sortBy: InboundEmailSortColumnSchema.optional(),
  sortDirection: z.enum(["asc", "desc"]).optional(),
});

export const ListInboundEmailsOutputSchema = z.object({
  edges: z.array(InboundEmailListItemSchema),
  pageInfo: InboundEmailListPageInfoSchema,
});

/**
 * Resolve the inbound email that owns a given document.
 *
 * Used by document viewers to detect when a document is a "docified" email
 * body (an HTML render of an inbound email's body) and pivot to the smart
 * email viewer instead of a raw file preview.
 */
export const GetInboundEmailByDocumentIdInputSchema = z.object({
  documentId: UUIDSchema,
});

export const GetInboundEmailByDocumentIdOutputSchema = z.object({
  /** The inbound email this document is associated with, or null if none. */
  inboundEmailId: UUIDSchema.nullable(),
  /** Conversation/thread id, when the source email has one. */
  conversationId: z.string().nullable(),
});

/**
 * Reprocess Inbound Email Input Schema
 */
export const ReprocessInboundEmailInputSchema = z.object({
  inboundEmailId: UUIDSchema,
  forceFreshExtraction: z
    .boolean()
    .optional()
    .describe(
      "Re-extract the email's documents instead of reusing their completed OCR results. Default false: the re-process reuses cached extractions.",
    ),
});

/**
 * Reprocess Inbound Email Output Schema
 */
export const ReprocessInboundEmailOutputSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
});

/**
 * Bulk Retrigger Email Rules Input Schema
 */
export const BulkRetriggerEmailRulesInputSchema = z
  .object({
    startDate: z
      .string()
      .datetime()
      .describe("ISO 8601 date string for start of range"),
    endDate: z
      .string()
      .datetime()
      .describe("ISO 8601 date string for end of range"),
    ruleIds: z
      .array(UUIDSchema)
      .optional()
      .describe(
        "Optional list of email rule IDs to retrigger. If not provided, all matching emails will be retriggered.",
      ),
    initialDelayMs: z
      .number()
      .int()
      .min(0)
      .max(300_000)
      .optional()
      .describe("Milliseconds to wait before processing the first email"),
    delayBetweenEmailsMs: z
      .number()
      .int()
      .min(0)
      .max(60_000)
      .optional()
      .describe("Milliseconds to wait between processing each email"),
  })
  .refine((data) => new Date(data.endDate) >= new Date(data.startDate), {
    message: "endDate must be greater than or equal to startDate",
    path: ["endDate"],
  });

/**
 * Bulk Retrigger Email Rules Output Schema
 */
export const BulkRetriggerEmailRulesOutputSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  emailsQueued: z.number().describe("Number of emails queued for reprocessing"),
});

/**
 * Cancel Bulk Retrigger Email Rules Output Schema (SD-83)
 */
export const CancelBulkRetriggerEmailRulesOutputSchema = z.object({
  emailsCancelled: z
    .number()
    .describe("Number of queued email re-triggers that were cancelled"),
});

/**
 * Preview Bulk Retrigger Email Rules Input Schema
 */
export const PreviewBulkRetriggerEmailRulesInputSchema = z
  .object({
    startDate: z
      .string()
      .datetime()
      .describe("ISO 8601 date string for start of range"),
    endDate: z
      .string()
      .datetime()
      .describe("ISO 8601 date string for end of range"),
    ruleIds: z
      .array(UUIDSchema)
      .optional()
      .describe(
        "Optional list of email rule IDs to filter by. If not provided, counts all emails in range.",
      ),
  })
  .refine((data) => new Date(data.endDate) >= new Date(data.startDate), {
    message: "endDate must be greater than or equal to startDate",
    path: ["endDate"],
  });

/**
 * Preview Bulk Retrigger Email Rules Output Schema
 */
export const PreviewBulkRetriggerEmailRulesOutputSchema = z.object({
  emailCount: z
    .number()
    .describe(
      "Number of emails that would be retriggered with current filters",
    ),
});

/**
 * Export Inbound Email For Debug
 *
 * Admin-only debug helper. Dumps a single inbound email in the *raw* state
 * it was in immediately after ingestion (i.e. what `ingestMicrosoftGraphEmailHandler`
 * persists to the DB). Attachment binaries themselves are NOT inlined — only
 * their `storageContainer`/`storagePath` is exported, since a local backend
 * pointed at the same cloud storage (which it must be, for any other email
 * to work locally) can read them in place.
 *
 * Intentionally excludes everything that downstream workflow steps produce:
 * classifications, OCR results, AI extractions, outbound replies, etc.
 */
export const ExportInboundEmailForDebugInputSchema = z.object({
  inboundEmailId: UUIDSchema,
});

const ExportedDocumentSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema.nullable().optional(),
  userId: UUIDSchema.nullable().optional(),
  fileSizeBytes: z.number().nullable().optional(),
  mimeType: z.string().nullable().optional(),
  status: z.string(),
  storageProvider: z.string(),
  storageLocation: z.string(),
  storageContainer: z.string(),
  storagePath: z.string(),
  originalFileName: z.string(),
  contentId: z.string().nullable().optional(),
  parentDocumentId: UUIDSchema.nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

const ExportedDocumentAssociationSchema = z.object({
  id: UUIDSchema,
  documentId: UUIDSchema,
  entityId: UUIDSchema,
  entityType: z.string(),
  associationType: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * Legacy attachment row kept so emails ingested via the old
 * `sendgrid-inbound-webhook` path can still be exported. New
 * `microsoft-graph-webhook` ingestion writes Documents instead.
 *
 * `s3Url` is optional for forward compatibility with older exports
 * that omitted it; on import we fall back to `s3Key` to satisfy the
 * model's NOT NULL constraint.
 */
const ExportedInboundEmailSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  senderUserId: UUIDSchema.nullable().optional(),
  messageId: z.string(),
  conversationId: z.string().nullable().optional(),
  graphMessageId: z.string().nullable().optional(),
  internetMessageId: z.string().nullable().optional(),
  from: z.string().nullable().optional(),
  to: z.string(),
  cc: z.string().nullable().optional(),
  subject: z.string().nullable().optional(),
  textContent: z.string().nullable().optional(),
  htmlContent: z.string().nullable().optional(),
  spamScore: z.number().nullable().optional(),
  spamReport: z.string().nullable().optional(),
  senderIp: z.string().nullable().optional(),
  spf: z.string().nullable().optional(),
  dkim: z.string().nullable().optional(),
  headers: z.string().nullable().optional(),
  envelope: z.string().nullable().optional(),
  charsets: z.string().nullable().optional(),
  rawPayload: z.unknown().nullable().optional(),
  attachmentCount: z.number().nullable().optional(),
  service: InboundEmailServiceSchema,
  receivedByMailbox: z.string().nullable().optional(),
  recipientSignals: z.string().nullable().optional(),
  detectedLanguage: z.string().nullable().optional(),
  languageConfidence: z.number().nullable().optional(),
  isSelfSent: z.boolean().nullable().optional(),
  linkedOutboundEmailId: UUIDSchema.nullable().optional(),
  originType: z.string().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  receivedAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * @deprecated The `comm_inbound_email_attachments` table was dropped in
 * Phase 8B of the file/document separation. This schema is retained for
 * backwards compatibility with v2 debug dumps that include legacy
 * attachment records; new exports always emit `legacyAttachments: []`.
 */
const ExportedLegacyAttachmentSchema = z.object({
  id: UUIDSchema,
  inboundEmailId: UUIDSchema,
  organizationId: UUIDSchema,
  originalName: z.string(),
  s3Key: z.string(),
  s3Url: z.string().optional(),
  mimeType: z.string(),
  sizeBytes: z.number(),
  contentId: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * Inlined original-attachment binary. Only "original" means
 * `associationType IN ('email_attachment', 'email')`, i.e. the file
 * attachments + the synthetic email-body Document that
 * `MicrosoftGraphEmailIngestWorker` produces. Split children
 * (`'transformed_file'`) are intentionally NOT included.
 */
const ExportedOriginalAttachmentSchema = z.object({
  documentId: UUIDSchema,
  fileName: z.string(),
  contentBase64: z.string(),
  contentLength: z.int().nonnegative(),
});

export const ExportInboundEmailForDebugOutputSchema = z.object({
  exportedAt: z.coerce.date(),
  // v2 = metadata only (legacy). v3 adds `originalAttachments` so the
  // dump can be replayed on a backend that does NOT share cloud storage
  // with the source.
  exportVersion: z.union([z.literal(2), z.literal(3)]),
  inboundEmail: ExportedInboundEmailSchema,
  documents: z.array(ExportedDocumentSchema),
  documentAssociations: z.array(ExportedDocumentAssociationSchema),
  /**
   * @deprecated Always empty since Phase 8B dropped the legacy attachments
   * table. Retained in the schema for backwards compatibility with existing
   * debug dump files.
   */
  legacyAttachments: z.array(ExportedLegacyAttachmentSchema),
  /**
   * Empty for v2 dumps. For v3, contains one entry per Document where
   * `associationType IN ('email_attachment', 'email')` — the import
   * handler uses these to upload bytes into local cloud storage.
   */
  originalAttachments: z.array(ExportedOriginalAttachmentSchema).default([]),
});

/**
 * Import Inbound Emails From Debug Dumps
 *
 * Admin-only debug helper. Reverses `exportForDebug`: takes an array of
 * export payloads and writes them into the local DB as if they had just
 * been ingested, so the user can then trigger reprocessing from
 * `splitDocumentsHandler`.
 *
 * Per-payload semantics (each runs in its own transaction):
 *   - `organizationId` is overridden with the importing user's org so
 *     the email is visible to them locally.
 *   - Skipped (not failed) when an InboundEmail with the same id already
 *     exists, or when the unique partial index
 *     `(organization_id, received_by_mailbox, message_id)` would conflict.
 *   - FKs that may not resolve in a local DB are nulled out:
 *     `senderUserId`, `linkedOutboundEmailId`, `Document.userId`, and
 *     `Document.parentDocumentId` (kept only if the parent is also in
 *     the same payload).
 *   - For v3 dumps the `originalAttachments` binaries are uploaded to
 *     the *local* cloud storage (`fileUploads.storageContainer`) under
 *     a freshly-generated key, and the resulting `Document` row points
 *     at that local container. v2 dumps preserve the original
 *     storagePath and assume the local backend is pointed at the same
 *     cloud storage as the source.
 */
/**
 * What to do when a payload's InboundEmail already exists in the local DB:
 *
 *  - `skip`: leave the existing row + its documents alone (default).
 *  - `refresh-attachments`: re-upload the dump's binaries into local cloud
 *    storage and point the existing Document rows at the new keys. Useful
 *    when developing against a clone of prod — the rows are already there,
 *    but their storage* fields point at the prod bucket the local backend
 *    can't read.
 *
 * Different-org collisions are always skipped regardless of this setting —
 * we won't mutate documents that belong to another organization.
 */
export const ImportOnDuplicateSchema = z
  .enum(["skip", "refresh-attachments"])
  .default("skip");

export const ImportInboundEmailFromDebugInputSchema = z.object({
  payloads: z.array(ExportInboundEmailForDebugOutputSchema).min(1).max(100),
  onDuplicate: ImportOnDuplicateSchema,
});

const ImportInboundEmailResultSchema = z.object({
  inboundEmailId: UUIDSchema,
  status: z.enum(["imported", "skipped", "refreshed", "failed"]),
  message: z.string().optional(),
});

export const ImportInboundEmailFromDebugOutputSchema = z.object({
  imported: z.int().nonnegative(),
  skipped: z.int().nonnegative(),
  refreshed: z.int().nonnegative(),
  failed: z.int().nonnegative(),
  results: z.array(ImportInboundEmailResultSchema),
});

/**
 * Inbound Email Contracts
 */
export const inboundEmailContracts = {
  list: oc
    .input(ListInboundEmailsInputSchema)
    .output(ListInboundEmailsOutputSchema)
    .route({
      method: "GET",
      summary: "List received emails",
      description:
        "Lists emails received by the organization's mailboxes, paged. Search or filter by sender, recipient, CC, subject, body text, or receiving mailbox, with sorting.",
    }),

  // Resolve the inbound email (and conversation) that owns a document.
  getByDocumentId: oc
    .input(GetInboundEmailByDocumentIdInputSchema)
    .output(GetInboundEmailByDocumentIdOutputSchema)
    .route({
      method: "GET",
      path: "/inbound-emails/by-document-id",
      summary: "Find the email behind a document",
      description:
        "Returns the received email and conversation that a document came from, such as an attachment or rendered email body, or null if none.",
    }),

  // Reprocess an inbound email
  reprocess: oc
    .input(ReprocessInboundEmailInputSchema)
    .output(ReprocessInboundEmailOutputSchema)
    .route({
      method: "POST",
      path: "/inbound-emails/reprocess",
      summary: "Reprocess a received email",
      description:
        "Queues a received email to run through email processing again, such as order document extraction, starting the configured workflow.",
    }),

  // Bulk retrigger email rules for emails in a date range (admin only)
  bulkRetriggerEmailRules: oc
    .input(BulkRetriggerEmailRulesInputSchema)
    .output(BulkRetriggerEmailRulesOutputSchema)
    .route({
      method: "POST",
      path: "/inbound-emails/bulk-retrigger-email-rules",
      summary: "Re-run email rules for a date range",
      description:
        "Re-runs email-rule workflows on emails received in a date range, optionally for specific rules. Emails are queued and released only while the organization's in-flight workflow runs stay below a cap; the delays are a minimum spacing. Requires Execute on Inbound Emails. Returns how many were queued.",
    }),

  // Cancel every queued bulk retrigger of the organization (SD-83)
  cancelBulkRetriggerEmailRules: oc
    .output(CancelBulkRetriggerEmailRulesOutputSchema)
    .route({
      method: "POST",
      path: "/inbound-emails/cancel-bulk-retrigger-email-rules",
      summary: "Cancel queued email-rule re-runs",
      description:
        "Cancels every email-rule re-run the organization has queued and not yet released. Emails already released keep running. Requires Execute on Inbound Emails. Returns how many were cancelled.",
    }),

  // Preview count of emails that would be retriggered (admin only)
  previewBulkRetriggerEmailRules: oc
    .input(PreviewBulkRetriggerEmailRulesInputSchema)
    .output(PreviewBulkRetriggerEmailRulesOutputSchema)
    .route({
      method: "POST",
      path: "/inbound-emails/preview-bulk-retrigger-email-rules",
      summary: "Count emails to re-run email rules on",
      description:
        "Counts emails in a date range, optionally limited to specific email rules, that a bulk email-rule re-run would process. Changes nothing. Requires Execute on Inbound Emails.",
    }),

  // Export a single email in its raw post-ingestion shape for local
  // debug (admin only). Attachment binaries are not inlined — only
  // their cloud storage location.
  exportForDebug: oc
    .input(ExportInboundEmailForDebugInputSchema)
    .output(ExportInboundEmailForDebugOutputSchema)
    .route({
      method: "POST",
      path: "/inbound-emails/export-for-debug",
      summary: "Export a received email for debugging",
      description:
        "Admin only: exports one received email as ingested, with its documents and original attachment files, for replaying in another environment. Excludes downstream processing results.",
    }),

  // Import previously-exported debug dumps into the local DB (admin only).
  importFromDebug: oc
    .input(ImportInboundEmailFromDebugInputSchema)
    .output(ImportInboundEmailFromDebugOutputSchema)
    .route({
      method: "POST",
      path: "/inbound-emails/import-from-debug",
      summary: "Import debug email exports",
      description:
        "Admin only: loads up to 100 exported email dumps into this environment as freshly ingested emails, uploading attachments. Existing emails are skipped or have attachments refreshed.",
    }),
} as const;

// Export types
export type InboundEmailListItem = z.infer<typeof InboundEmailListItemSchema>;
export type ListInboundEmailsInput = z.infer<
  typeof ListInboundEmailsInputSchema
>;
export type ListInboundEmailsOutput = z.infer<
  typeof ListInboundEmailsOutputSchema
>;
export type GetInboundEmailByDocumentIdInput = z.infer<
  typeof GetInboundEmailByDocumentIdInputSchema
>;
export type GetInboundEmailByDocumentIdOutput = z.infer<
  typeof GetInboundEmailByDocumentIdOutputSchema
>;
export type ReprocessInboundEmailInput = z.infer<
  typeof ReprocessInboundEmailInputSchema
>;
export type ReprocessInboundEmailOutput = z.infer<
  typeof ReprocessInboundEmailOutputSchema
>;
export type BulkRetriggerEmailRulesInput = z.infer<
  typeof BulkRetriggerEmailRulesInputSchema
>;
export type BulkRetriggerEmailRulesOutput = z.infer<
  typeof BulkRetriggerEmailRulesOutputSchema
>;
export type PreviewBulkRetriggerEmailRulesInput = z.infer<
  typeof PreviewBulkRetriggerEmailRulesInputSchema
>;
export type PreviewBulkRetriggerEmailRulesOutput = z.infer<
  typeof PreviewBulkRetriggerEmailRulesOutputSchema
>;
export type ExportInboundEmailForDebugInput = z.infer<
  typeof ExportInboundEmailForDebugInputSchema
>;
export type ExportInboundEmailForDebugOutput = z.infer<
  typeof ExportInboundEmailForDebugOutputSchema
>;
export type ExportedOriginalAttachment = z.infer<
  typeof ExportedOriginalAttachmentSchema
>;
export type ImportInboundEmailFromDebugInput = z.infer<
  typeof ImportInboundEmailFromDebugInputSchema
>;
export type ImportInboundEmailFromDebugOutput = z.infer<
  typeof ImportInboundEmailFromDebugOutputSchema
>;
export type ImportInboundEmailResult = z.infer<
  typeof ImportInboundEmailResultSchema
>;
export type ImportOnDuplicate = z.infer<typeof ImportOnDuplicateSchema>;
